import {
  ArrowDownward as DownIcon,
  ArrowUpward as UpIcon,
  WarningAmberRounded,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useQuery } from '@tanstack/react-query';

import { MemoSection } from '@/components/residents/detail/MemoSection';
import {
  type BgtContentCard,
  type BgtTodayDetail,
  type BgtTodaySummary,
  getResidentBgtToday,
} from '@/lib/api/bgt-today';
import type { ResidentDto } from '@/lib/api/residents';
import {
  type VoiceScoreCard,
  type VoiceTodayDetail,
  getResidentVoiceToday,
} from '@/lib/api/voice-today';

interface Props {
  resident: ResidentDto | null;
}

function formatDuration(ms: number): string {
  if (ms <= 0) return '0분';
  const min = Math.floor(ms / 60000);
  if (min < 60) return `${min}분`;
  const h = Math.floor(min / 60);
  const remM = min % 60;
  return remM > 0 ? `${h}시간 ${remM}분` : `${h}시간`;
}

function formatScore(v: number | null): string {
  return v === null ? '—' : `${v}점`;
}

function DeltaBadge({ deltaPct, dropped }: { deltaPct: number | null; dropped: boolean }) {
  if (deltaPct === null) {
    return (
      <Typography variant="caption" color="text.disabled">
        비교 자료 없음
      </Typography>
    );
  }
  if (deltaPct === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        전일과 동일
      </Typography>
    );
  }
  const isDown = deltaPct < 0;
  const color = dropped ? 'error.main' : isDown ? 'warning.main' : 'success.main';
  const Icon = isDown ? DownIcon : UpIcon;
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      {dropped && <WarningAmberRounded sx={{ color: 'error.main', fontSize: 16 }} />}
      <Icon sx={{ color, fontSize: 16 }} />
      <Typography variant="caption" sx={{ color, fontWeight: 600 }}>
        {Math.abs(deltaPct)}%
      </Typography>
    </Stack>
  );
}

function SummaryRow({ summary }: { summary: BgtTodaySummary }) {
  const cells: { label: string; value: string; sub?: React.ReactNode }[] = [
    {
      label: '총 사용시간',
      value: formatDuration(summary.totalUseTime),
    },
    {
      label: '문제 수',
      value: `${summary.testCount}`,
    },
    {
      label: '평균 점수',
      value: formatScore(summary.avgScore),
      sub: <DeltaBadge deltaPct={summary.avgDeltaPct} dropped={summary.avgDropped} />,
    },
  ];
  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            오늘 요약
          </Typography>
          <Stack direction="row" spacing={1}>
            <Chip size="small" label={summary.date} variant="outlined" />
            <Chip
              size="small"
              label={
                summary.source === 'cache'
                  ? '집계 캐시'
                  : summary.source === 'live'
                    ? '실시간'
                    : '데이터 없음'
              }
              color={summary.source === 'empty' ? 'default' : 'primary'}
              variant="outlined"
            />
          </Stack>
        </Stack>
        <Grid container spacing={2}>
          {cells.map((c) => (
            <Grid size={{ xs: 4 }} key={c.label}>
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  {c.label}
                </Typography>
                <Typography variant="h5">{c.value}</Typography>
                {c.sub}
              </Stack>
            </Grid>
          ))}
        </Grid>
        {!summary.hasData && (
          <>
            <Divider sx={{ my: 2 }} />
            <Alert severity="info" variant="outlined">
              오늘은 아직 BGT 사용 기록이 없습니다. 어제 점수를 baseline으로 표시합니다.
            </Alert>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function ContentCardItem({ card }: { card: BgtContentCard }) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent sx={{ pb: '16px !important' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            {card.label}
          </Typography>
          {card.level !== null && (
            <Chip size="small" label={`난이도 ${card.level}`} variant="outlined" />
          )}
        </Stack>
        <Typography variant="h4" sx={{ mb: 0.5 }}>
          {formatScore(card.todayScore)}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="caption" color="text.secondary">
            어제 {formatScore(card.yesterdayScore)}
          </Typography>
          <DeltaBadge deltaPct={card.deltaPct} dropped={card.dropped} />
        </Stack>
      </CardContent>
    </Card>
  );
}

/**
 * 의뢰서 5-2 탭1 오늘 현황 (P4-2).
 *
 * 표시 항목:
 *   - 상단 요약: 사용시간 / 문제 수 / 평균 점수 (+ 전일 대비 등락)
 *   - 콘텐츠 5종 카드: 문장완성 / 문장질문 / 단어맞추기(=WORD+NOISE_WORD 평균) /
 *                     단어순서기억 / 음소훈련
 *
 * 데이터: GET /residents/:id/bgt/today
 */
export function TabToday({ resident }: Props) {
  const { data, isLoading } = useQuery<BgtTodayDetail>({
    queryKey: ['residents', 'bgt-today', resident?.id],
    queryFn: () => getResidentBgtToday(resident!.id),
    enabled: !!resident,
    staleTime: 30 * 1000,
  });

  if (!resident) return null;

  if (isLoading) {
    return (
      <Stack spacing={2}>
        <Skeleton variant="rounded" height={120} />
        <Grid container spacing={2}>
          {[0, 1, 2, 3, 4].map((i) => (
            <Grid size={{ xs: 12, sm: 6 }} key={i}>
              <Skeleton variant="rounded" height={110} />
            </Grid>
          ))}
        </Grid>
      </Stack>
    );
  }

  if (!data) return null;

  return (
    <Stack spacing={2}>
      <SummaryRow summary={data.summary} />
      <Box>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
          콘텐츠별 점수
        </Typography>
        <Grid container spacing={2}>
          {data.contents.map((c) => (
            <Grid size={{ xs: 12, sm: 6 }} key={c.type}>
              <ContentCardItem card={c} />
            </Grid>
          ))}
        </Grid>
      </Box>
      <VoiceWellnessToday residentId={resident.id} />
      <MemoSection residentId={resident.id} />
    </Stack>
  );
}

/**
 * 보이스웰니스 오늘 현황
 */
function VoiceWellnessToday({ residentId }: { residentId: string }) {
  const { data, isLoading } = useQuery<VoiceTodayDetail>({
    queryKey: ['residents', 'voice-today', residentId],
    queryFn: () => getResidentVoiceToday(residentId),
    staleTime: 30 * 1000,
  });

  if (isLoading) {
    return <Skeleton variant="rounded" height={140} />;
  }
  if (!data) return null;

  const noDataMessage =
    data.voiceUserId == null
      ? '보이스웰니스 계정이 연결되지 않았습니다. 입소자 수정에서 계정을 연결하세요.'
      : '오늘은 보이스웰니스 완료 세션이 없습니다.';

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            보이스웰니스 오늘 현황
          </Typography>
          <Stack direction="row" spacing={1}>
            <Chip size="small" label={data.summary.date} variant="outlined" />
            <Chip
              size="small"
              label={
                data.summary.source === 'cache'
                  ? '집계 캐시'
                  : data.summary.source === 'live'
                    ? '실시간'
                    : '데이터 없음'
              }
              color={data.summary.source === 'empty' ? 'default' : 'primary'}
              variant="outlined"
            />
          </Stack>
        </Stack>
        {!data.summary.hasData && (
          <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
            {noDataMessage}
          </Alert>
        )}
        <Grid container spacing={2}>
          {data.scores.map((card) => (
            <Grid size={{ xs: 12, sm: 4 }} key={card.type}>
              <VoiceScoreCardItem card={card} />
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
}

function VoiceScoreCardItem({ card }: { card: VoiceScoreCard }) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Stack spacing={0.5}>
          <Typography variant="caption" color="text.secondary">
            {card.label}
            {card.status ? ` · ${card.status}` : ''}
          </Typography>
          <Typography variant="h5">{formatScore(card.todayScore)}</Typography>
          <DeltaBadge deltaPct={card.deltaPct} dropped={card.dropped} />
        </Stack>
      </CardContent>
    </Card>
  );
}
